import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSales } from "./hooks";
import { PageHeader } from "../../components/PageHeader";
import { AlertCircleIcon, BanknoteIcon, CalendarIcon, CreditCardIcon, HistoryIcon, InfoCircleIcon, NotebookPenIcon, ArrowLeftRightIcon, ChevronRightIcon, SplitPaymentIcon } from "../../components/icons";
import { formatARS } from "../../lib/money";
import { MovementsHistoryPanel } from "../movements/MovementsHistoryPanel";

function todayLocalISO() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function PaymentIcon({ color, divided = false }: { color: string; divided?: boolean }) {
  if (divided) return <SplitPaymentIcon />;
  if (color === "cash") return <BanknoteIcon />;
  if (color === "transfer") return <ArrowLeftRightIcon />;
  if (color === "card") return <CreditCardIcon />;
  return <NotebookPenIcon />;
}

export function SalesHistoryView() {
  const navigate = useNavigate();
  const [date, setDate] = useState(todayLocalISO());
  const [tab, setTab] = useState<"sales" | "movements">("sales");
  const sales = useSales({ from: `${date}T00:00:00.000Z`, to: `${date}T23:59:59.999Z`, limit: 200 });
  const activeSales = sales.data?.filter((sale) => sale.status === "active").length ?? 0;
  const total = sales.data?.reduce((sum, sale) => sum + (sale.status === "active" ? sale.totalChargedCents : 0), 0) ?? 0;

  return (
    <main className="page page--narrow">
      <PageHeader title="Historial" />
      <section className="page-section">
        <div className="history-toolbar">
          <label className="date-control"><CalendarIcon /><input type="date" aria-label="Fecha" value={date} onChange={(event) => setDate(event.target.value)} /></label>
          {date !== todayLocalISO() && <button type="button" onClick={() => setDate(todayLocalISO())}>Volver a hoy</button>}
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
          <button type="button" onClick={() => setTab("sales")} style={tabStyle(tab === "sales")}>Ventas</button>
          <button type="button" onClick={() => setTab("movements")} style={tabStyle(tab === "movements")}>Movimientos</button>
        </div>
        {tab === "movements" ? <MovementsHistoryPanel date={date} /> : <>
          <div className="history-kpis"><span><small>Ventas activas</small><strong>{activeSales}</strong></span><span><small>Total cobrado</small><strong className="num">{formatARS(total)}</strong></span></div>
          {sales.isLoading && <div className="skeleton-list"><span /><span /><span /></div>}
          {sales.data?.length === 0 && <div className="empty-state"><HistoryIcon width={28} height={28} /><strong>Sin ventas</strong><span>No se registraron operaciones en esta fecha.</span></div>}
          <div className="sales-list">
            {sales.data?.map((sale) => {
            const pending = sale.status === "active" && sale.paymentMethodSnapshot.requiresNetReceived && sale.netReceivedCents === null;
            // Distinto de "pendiente de neto": acá el medio se cobra por
            // liquidación (ej. ClubCred) y el pago todavía no fue incluido
            // en ninguna liquidación confirmada. Chequeo por pago porque una
            // venta dividida puede tener un solo leg de este tipo.
            const pendingSettlement =
              sale.status === "active" && sale.payments.some((p) => p.paymentMethodSnapshot.settlesInBatch && p.settlementId === null);
            const units = sale.items.reduce((sum, item) => sum + item.qty, 0);
            return (
              <button className={`sale-history-card payment-${sale.paymentMethodSnapshot.color}${sale.status === "voided" ? " is-voided" : ""}`} key={sale.id} type="button" onClick={() => navigate(`/sales/${sale.id}`)}>
                <span className="sale-history-card__icon"><PaymentIcon color={sale.paymentMethodSnapshot.color} divided={sale.payments.length > 1} /></span>
                <span className="sale-history-card__main"><strong>Venta #{sale.id}</strong><small>{new Date(sale.createdAt).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })} · {units} {units === 1 ? "unidad" : "unidades"}</small><em>{sale.paymentMethodSnapshot.name}</em></span>
                <span className="sale-history-card__amount"><strong className="num">{formatARS(sale.totalChargedCents)}</strong>{pending && <small className="status-badge status-badge--warning"><AlertCircleIcon /> Neto pendiente</small>}{pendingSettlement && <small className="status-badge status-badge--info"><InfoCircleIcon /> Pendiente de liquidación</small>}{sale.status === "voided" && <small className="status-badge status-badge--danger">Anulada</small>}</span>
                <ChevronRightIcon />
              </button>
            );
            })}
          </div>
        </>}
      </section>
    </main>
  );
}

function tabStyle(active: boolean): React.CSSProperties {
  return {
    minHeight: 42,
    padding: "0 var(--space-4)",
    borderRadius: "var(--radius-pill)",
    border: "1px solid var(--border-default)",
    background: active ? "var(--text-primary)" : "var(--surface-raised)",
    color: active ? "var(--text-inverse)" : "var(--text-primary)",
    fontWeight: 600,
    cursor: "pointer",
  };
}
